import { useQuery } from '@tanstack/react-query'
import type { SessionUser } from '@lms/shared-types'
import { ApiError } from '../api/client'
import {
  MICROSOFT_LOGIN_PATH,
  fetchSessionUser,
  signOutSession,
} from './auth-client'

/**
 * Auth model
 * ----------
 * The FastAPI api owns both providers:
 *   - email/password (dev only — `DEV_PASSWORD_AUTH_ENABLED`)
 *   - Microsoft Entra OIDC (production identity provider)
 *
 * The web client holds no auth state of its own. `useSession` is a TanStack
 * Query against `/api/v1/auth/me`; the api answers from the httpOnly session
 * cookie. 401 is treated as "not signed in" (returns null), other errors
 * bubble up.
 *
 *   1. LoginPage submits → signInWithPassword(email, password)
 *      OR navigates to MICROSOFT_LOGIN_PATH for Entra
 *   2. The api sets the `lms_session` httpOnly cookie
 *   3. useSession() resolves the SessionUser shape (role, teamId, …)
 */

const SESSION_QUERY_KEY = ['session', 'me'] as const

async function fetchMe(): Promise<SessionUser | null> {
  try {
    return await fetchSessionUser()
  } catch (error: unknown) {
    if (error instanceof ApiError && error.status === 401) {
      return null
    }
    throw error
  }
}

export interface SessionState {
  user: SessionUser | null
  isLoading: boolean
  isError: boolean
  error: unknown
  refetch: () => void
}

export function useSession(): SessionState {
  const query = useQuery({
    queryKey: SESSION_QUERY_KEY,
    queryFn: fetchMe,
    retry: false,
    staleTime: 60_000,
  })

  return {
    user: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: () => {
      void query.refetch()
    },
  }
}

export async function startMicrosoftLogin(): Promise<void> {
  // Server-side OIDC flow — the api redirects to Entra and handles the
  // callback. We just navigate the browser away. Carry any `?next=` (e.g.
  // the connector-verify continuation) so the callback can resume it.
  const next = new URLSearchParams(window.location.search).get('next')
  const url = next
    ? `${MICROSOFT_LOGIN_PATH}?next=${encodeURIComponent(next)}`
    : MICROSOFT_LOGIN_PATH
  window.location.assign(url)
}

export async function startLogout(): Promise<void> {
  try {
    await signOutSession()
  } finally {
    window.location.assign('/login')
  }
}
